import { type ReactNode } from 'react'

import { Icon } from './Icon'
import { cn } from '../lib/cn'

/**
 * Search bar — ported from `FgSearchBar` in `designs/system.jsx` (~438), which
 * M16 derived from nodes `710:1143` / `713:1642` (Tourism hero) and `88:689`
 * (Local).
 *
 * PORTED: pill shell on `--bg-app` with a `--line-1` border, 6px padding with
 * 16px of lead-in, and the segmented variant's leading tablist divided from
 * the field by a `--line-1` rule.
 *
 * The incumbent field used `rounded-[10px]`, a literal that was correct when
 * `--radius-r3` was 10px. T03 moved that token to 16px; upstream draws this
 * shell as a pill regardless, so it is `rounded-full` here rather than a
 * radius token.
 */
export type SearchVariant = 'single' | 'segmented'

export interface SearchInputProps {
  value: string
  onChange: (value: string) => void
  /** Accessible name for the input (also used if no visible label). */
  label?: string
  placeholder?: string
  /**
   * `single` is one field. `segmented` puts a category tablist inside the
   * shell, for a surface that searches across two entity types.
   */
  variant?: SearchVariant
  /** Tab labels for the `segmented` variant. Ignored by `single`. */
  tabs?: string[]
  activeTab?: string
  onTabChange?: (tab: string) => void
  /** Accessible name for the tablist. */
  tabsLabel?: string
  /**
   * Trailing submit button. DELIBERATELY OPTIONAL — see the note where it is
   * rendered.
   */
  onSearch?: () => void
  className?: string
}

export function SearchInput({
  value,
  onChange,
  label = 'Search hospitals',
  placeholder = 'Search hospitals, areas, services',
  variant = 'single',
  tabs = [],
  activeTab,
  onTabChange,
  tabsLabel = 'Category',
  onSearch,
  className,
}: SearchInputProps): ReactNode {
  const segmented = variant === 'segmented' && tabs.length > 0

  return (
    <div
      className={cn(
        // `min-w-0` is load-bearing, not tidiness (M20 finding F3). As a flex
        // child this shell's `min-width: auto` floors it at its own min-content
        // — which includes the `<input>`'s intrinsic 20-character width (215px
        // at 16px). The input's own `min-w-0` only lets the input shrink INSIDE
        // the shell; it never lets the shell itself be squeezed. On
        // `HospitalResults` that floored the row at 369.7px inside a 343px
        // container and pushed the "Near me" button off-screen: measured at a
        // true 390px viewport, `scrollWidth` 386 against `clientWidth` 375.
        // Harmless at the column call sites — outside a flex/grid item
        // `min-width: auto` already computes to 0.
        'flex min-w-0 items-center gap-2 rounded-full border border-line-1 bg-bg-app py-1.5 pl-4 pr-1.5',
        'focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-accent',
        className,
      )}
    >
      {segmented ? (
        <div
          role="tablist"
          aria-label={tabsLabel}
          className="flex gap-1 border-r border-line-1 pr-2"
        >
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={t === activeTab}
              onClick={() => onTabChange?.(t)}
              className={cn(
                'rounded-full px-3 py-1.5 text-[14px] leading-5',
                t === activeTab
                  ? 'bg-accent-bg font-medium text-accent-ink'
                  : 'text-ink-3 hover:text-ink-1',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
              )}
            >
              {t}
            </button>
          ))}
        </div>
      ) : (
        // The magnifier is INCUMBENT, not from the artboards — upstream leans
        // on the trailing accent button for the affordance. It is kept for the
        // no-button case below, which is every current call site: a bare pill
        // with no glyph and no button does not read as a search field.
        <span className="shrink-0 text-ink-3" aria-hidden="true">
          <Icon name="search" size={16} />
        </span>
      )}

      <input
        type="search"
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="min-w-0 flex-1 border-0 bg-transparent text-[16px] leading-6 text-ink-1 outline-none placeholder:text-ink-4"
      />

      {/* DEVIATION, deliberate. Upstream always draws the round accent submit
          button. Every search in this app is a live filter — the list narrows
          as you type, there is nothing to submit — so rendering it
          unconditionally would put a button on screen that does nothing.
          A dead control is worse than an absent one, so it appears only where
          a caller actually has a submit action. M22-T09 gave it its first:
          the Local hero NAVIGATES rather than filtering, and Enter alone was
          not an affordance a patient on a phone would find;
          the prop exists for the tourism hero, which is a real submit.
          Search behaviour itself is out of scope for M17-T05. */}
      {onSearch ? (
        <button
          type="button"
          aria-label="Search"
          onClick={onSearch}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent text-accent-on hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="search" size={16} />
        </button>
      ) : null}
    </div>
  )
}
